import React from "react";
import { Card, Typography, Button, Space, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useTranslate, useNavigation } from "@refinedev/core";
import { snakeToCamel } from "@/utils/fieldMapping";
import { BooleanToggleCell } from "./BooleanToggleCell";
import type {
  RelationshipItemData,
  RelationshipGridColumn,
  RelationshipMetadata,
} from "@/types";

const { Text, Link } = Typography;

interface RelationshipCardProps {
  record: RelationshipItemData;
  relationship: RelationshipMetadata;
  booleanColumns: RelationshipGridColumn[];
  nonBooleanColumns: RelationshipGridColumn[];
  loadingCells: Map<string, boolean>;
  bulkLoadingColumns: Map<string, boolean>;
  onBooleanToggle: (
    record: RelationshipItemData,
    columnName: string,
    newValue: boolean,
  ) => void;
  onEdit: (record: RelationshipItemData) => void;
  onDelete: (record: RelationshipItemData) => void;
  isDeleteLoading: boolean;
}

export const RelationshipCard: React.FC<RelationshipCardProps> = ({
  record,
  relationship,
  booleanColumns,
  nonBooleanColumns,
  loadingCells,
  bulkLoadingColumns,
  onBooleanToggle,
  onEdit,
  onDelete,
  isDeleteLoading,
}) => {
  const translate = useTranslate();
  const { show } = useNavigation();

  // Extract display name from related item
  const related = record.relatedItemProperties || {};
  const displayName =
    (related.keyedName as string) ||
    (related.name as string) ||
    record.relatedId ||
    record.id;
  const itemNumber = related.itemNumber as string | undefined;

  const actions = (
    <Space size={4} onClick={(e) => e.stopPropagation()}>
      <Button
        type="text"
        size="small"
        icon={<EditOutlined />}
        onClick={() => onEdit(record)}
        aria-label={translate("buttons.edit", "Edit")}
      />
      <Popconfirm
        title={translate("buttons.confirmDelete", "Are you sure?")}
        onConfirm={() => onDelete(record)}
        okText={translate("buttons.yes", "Yes")}
        cancelText={translate("buttons.no", "No")}
      >
        <Button
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          loading={isDeleteLoading}
          aria-label={translate("buttons.delete", "Delete")}
        />
      </Popconfirm>
    </Space>
  );

  const handleNameClick = () => {
    if (relationship.relatedItemTypeName && record.relatedId) {
      show(relationship.relatedItemTypeName, record.relatedId);
    }
  };

  return (
    <Card
      size="small"
      styles={{ body: { padding: "12px 16px" } }}
      style={{ height: "100%" }}
    >
      {/* Header: Name + Actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {relationship.relatedItemTypeName && record.relatedId ? (
            <Link
              onClick={handleNameClick}
              style={{ fontSize: 15, fontWeight: 600, display: "block" }}
            >
              {displayName}
            </Link>
          ) : (
            <Text strong style={{ fontSize: 15, display: "block" }}>
              {displayName}
            </Text>
          )}
          {itemNumber && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {itemNumber}
            </Text>
          )}
        </div>
        {actions}
      </div>

      {/* Boolean toggle rows */}
      {booleanColumns.map((col) => {
        const propName = snakeToCamel(col.name);
        const value =
          record.relationshipProperties?.[propName] ??
          record.relatedItemProperties?.[propName];
        const checked =
          value === true || value === "1" || value === 1 || value === "true";
        const cellKey = `${record.id}_${col.name}`;
        const isLoading =
          loadingCells.get(cellKey) || bulkLoadingColumns.get(col.name);

        return (
          <div
            key={col.key}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Text style={{ fontSize: 14 }}>{col.label}</Text>
            <BooleanToggleCell
              checked={checked}
              loading={isLoading}
              disabled={col.isReadOnly || bulkLoadingColumns.get(col.name)}
              onChange={(newValue) =>
                onBooleanToggle(record, col.name, newValue)
              }
              ariaLabel={`${col.label} for ${displayName}`}
            />
          </div>
        );
      })}

      {/* Non-boolean fields (compact) */}
      {nonBooleanColumns.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {nonBooleanColumns.map((col) => {
            const propName = snakeToCamel(col.name);
            const value =
              col.source === "related"
                ? related[propName]
                : record.relationshipProperties?.[propName];
            if (value === undefined || value === null || value === "")
              return null;

            return (
              <div key={col.key} style={{ marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {col.label}
                </Text>
                <div style={{ fontSize: 13 }}>{String(value)}</div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default RelationshipCard;
